import { cn } from "@/lib/utils.ts";
import { atom, useSetAtom } from "jotai";
import { useCallback, useRef } from "react";

/**
 * Where in the layout a publishing header sits, left to right on a desktop.
 *
 * The utilities belong to the screen's upper right, and on a desktop that is
 * whichever of these columns is furthest right on screen. A card on the canvas
 * outranks a sidebar, which outranks the page in the middle column. On a phone
 * there is one column at a time and the same order still holds: a sidebar or a
 * card is covering the page rather than sitting beside it.
 *
 * At most one header per rank may be mounted at a time, which every publisher
 * relies on: within a rank the tie goes to whoever published last, and mount
 * order is not a layout fact. Where two could coexist, the one that is not the
 * screen's corner declines to publish — `ThreadListPanel` on a desktop, a card
 * whose canvas belongs to a panel rather than to the screen.
 */
const LAYER_RANKS = {
  /** The middle column's own header, and the site header on screens without one. */
  page: 0,
  /** The right sidebar, beside the page on a desktop and over it on a phone. */
  sidebar: 1,
  /** The canvas column, and a card filling a phone's screen. */
  canvas: 2,
} as const;

export type HeaderUtilityLayer = keyof typeof LAYER_RANKS;

type Publication = { el: HTMLElement; rank: number };

/**
 * Every header currently offering to hold the utilities.
 *
 * A stack rather than one stored element, because these hosts nest rather than
 * replace, and React runs a ref only when its own element mounts or unmounts.
 * One element could be taken and never given back: the host underneath never
 * moved, so closing the overlay above it leaves the slot pointing at a detached
 * node with nobody left to re-claim it.
 */
const stackAtom = atom<Publication[]>([]);

/**
 * The element to portal the utilities into: the topmost header still mounted.
 *
 * Highest rank wins, and within a rank the one that published last — which is
 * what settles two cards on the same canvas. Rank rather than order alone,
 * because order is only right while the upper layers are the newer ones:
 * navigating the page column with a card open remounts the page header last,
 * and the controls would leave the card for a column nobody had touched.
 *
 * Null only on a screen that renders no header at all, where the controls fall
 * back to their own fixed corner.
 */
export const headerUtilitySlotAtom = atom(
  (get) =>
    get(stackAtom).reduce<Publication | null>(
      (best, entry) => (!best || entry.rank >= best.rank ? entry : best),
      null,
    )?.el ?? null,
);

/**
 * Where the header utilities land in this header: the bug-report button, and
 * the React Query devtools toggle on a development build.
 *
 * A portal target rather than the controls themselves, because only one set may
 * be on screen. Several headers publish — a page, a sidebar beside it, a card
 * over both — and the controls belong to whichever is topmost. Rendering them
 * inline here would put a copy in every header that is currently mounted.
 *
 * `layer` says which band of the layout this header is, and the topmost band
 * wins — see {@link LAYER_RANKS}. It defaults to the page, which is what a
 * screen's own header is unless it is a sidebar or a canvas.
 *
 * `empty:hidden` because an empty flex item still takes the row's gap, which
 * would hold the control beside it off the gutter its content lines up on.
 */
export const HeaderUtilitySlot = ({
  layer = "page",
  className,
}: {
  layer?: HeaderUtilityLayer;
  className?: string;
}) => {
  const setStack = useSetAtom(stackAtom);
  // The element this host last published, so its own entry can be found again
  // when React hands the ref a `null` on unmount.
  const ownEl = useRef<HTMLElement | null>(null);
  const rank = LAYER_RANKS[layer];

  const publish = useCallback(
    (el: HTMLElement | null) => {
      const previous = ownEl.current;
      ownEl.current = el;
      setStack((stack) => {
        const without = previous ? stack.filter((entry) => entry.el !== previous) : stack;
        return el ? [...without, { el, rank }] : without;
      });
    },
    [setStack, rank],
  );

  return (
    <div
      ref={publish}
      className={cn("flex shrink-0 items-center gap-0.5 empty:hidden", className)}
    />
  );
};
